import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, InputGroup, Modal, Row, Spinner } from 'react-bootstrap';
import {
  chatCreateProject,
  chatGetProjectSecrets,
  chatGetWidgetStatus,
  chatRegenerateWidgetSecret,
  ChatApiError,
  type ChatProjectSecrets,
  type ChatProjectWidgetStatus
} from 'helpers/chatApi';

// Конструктор widget snippet для встраивания support-chat на сайт/в
// приложение клиента проекта. Шаблон snippet — support-chat/docs/12-
// widget-embed-guide.md:
//
//   <script
//     src="https://support-chatwidget-test.up.railway.app/widget.js"
//     data-backend-url="https://support-chat-test.up.railway.app"
//     data-project-id="<PROJECT_ID>"
//     data-shared-secret="<SHARED_SECRET>"
//     async
//   ></script>
//
// v67 (2026-05-29): подключены chat-backend admin endpoints (§11 doc 13,
// реализованы chat-командой 2026-05-28). При mount компонент тянет:
//   GET /api/admin/projects/:id/secrets       — widget_shared_secret
//   GET /api/admin/projects/:id/widget-status — индикатор «установлен»
// Если проекта нет на chat-backend (404) — создаёт через POST. Кнопка
// «Перевыпустить secret» — POST /api/admin/projects/:id/regenerate-widget-
// secret?confirm=true с confirm-modal (destructive: старые embed-snippet'ы
// сломаются).

interface Props {
  projectId: string;
  projectName: string;
}

const DEFAULT_WIDGET_URL = 'https://support-chatwidget-test.up.railway.app/widget.js';
const DEFAULT_BACKEND_URL = 'https://support-chat-test.up.railway.app';
const SECRET_PLACEHOLDER = '<SHARED_SECRET>';

const ChatWidgetTab: React.FC<Props> = ({ projectId, projectName }) => {
  const widgetUrl =
    (process.env.REACT_APP_CHAT_WIDGET_URL as string | undefined) ||
    DEFAULT_WIDGET_URL;
  const backendUrl =
    (process.env.REACT_APP_CHAT_BACKEND_URL as string | undefined) ||
    DEFAULT_BACKEND_URL;

  const [secrets, setSecrets] = useState<ChatProjectSecrets | null>(null);
  const [widgetStatus, setWidgetStatus] = useState<ChatProjectWidgetStatus | null>(null);
  const [loadingState, setLoadingState] = useState<'idle' | 'loading' | 'ok' | 'error' | 'not_created'>('idle');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [regenLoading, setRegenLoading] = useState(false);
  const [showRegenConfirm, setShowRegenConfirm] = useState(false);

  // Загрузка secrets + widget status. 404 secrets → проекта в chat-backend
  // ещё нет, предлагаем создать через POST (см. handleCreate).
  const loadAll = useCallback(async () => {
    setLoadingState('loading');
    setErrorMsg('');
    try {
      const [s, ws] = await Promise.all([
        chatGetProjectSecrets(projectId),
        chatGetWidgetStatus(projectId)
      ]);
      setSecrets(s);
      setWidgetStatus(ws);
      if (s === null) {
        setLoadingState('not_created');
      } else {
        setLoadingState('ok');
      }
    } catch (e) {
      // ChatApiError'ом из chatApi.ts помечает stage:
      //  - sso_token → admin-api не выдал JWT (CHAT_JWT_SECRET, 401/403).
      //  - chat_backend → CORS / down / 4xx-5xx.
      // Раньше всё кидалось как axios «Network Error» — оператор путал.
      if (e instanceof ChatApiError) {
        if (e.stage === 'sso_token') {
          setErrorMsg(`SSO-токен от admin-api: ${e.message}`);
        } else {
          setErrorMsg(`Chat-backend: ${e.message}`);
        }
      } else {
        const err = e as { message?: string; response?: { status?: number } };
        setErrorMsg(err?.message || 'Неизвестная ошибка');
      }
      setLoadingState('error');
    }
  }, [projectId]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const handleCreate = async () => {
    setLoadingState('loading');
    setErrorMsg('');
    try {
      await chatCreateProject(projectId, projectName || projectId);
      await loadAll();
    } catch (e) {
      const err = e as { message?: string };
      setErrorMsg(err?.message || 'Не удалось создать проект в chat-backend');
      setLoadingState('error');
    }
  };

  const handleRegenerate = async () => {
    setShowRegenConfirm(false);
    setRegenLoading(true);
    setErrorMsg('');
    try {
      const fresh = await chatRegenerateWidgetSecret(projectId);
      setSecrets(fresh);
    } catch (e) {
      const err = e as { message?: string };
      setErrorMsg(err?.message || 'Не удалось перевыпустить secret');
    } finally {
      setRegenLoading(false);
    }
  };

  const sharedSecret = secrets?.widget_shared_secret || '';
  const effectiveSecret = sharedSecret || SECRET_PLACEHOLDER;

  const snippet = useMemo(() => {
    return [
      '<script',
      `  src="${widgetUrl}"`,
      `  data-backend-url="${backendUrl}"`,
      `  data-project-id="${projectId}"`,
      `  data-shared-secret="${effectiveSecret}"`,
      '  async',
      '></script>'
    ].join('\n');
  }, [widgetUrl, backendUrl, projectId, effectiveSecret]);

  const copy = (text: string, key: string) => {
    void navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    });
  };

  // Статус виджета: бейдж + tooltip. installed=true + last_seen_at < 24h →
  // «Установлен» (зелёный), иначе — «Не установлен» (серый).
  const statusBadge = (() => {
    if (loadingState === 'loading' || loadingState === 'idle') {
      return <Spinner animation="border" size="sm" />;
    }
    if (!widgetStatus) return null;
    if (widgetStatus.installed) {
      const lastSeen = widgetStatus.last_seen_at
        ? new Date(widgetStatus.last_seen_at).toLocaleString('ru-RU')
        : '—';
      return (
        <Badge bg="success" className="small" title={`Последняя активность: ${lastSeen}`}>
          Установлен · {widgetStatus.active_sessions_24h} сессий за 24ч
        </Badge>
      );
    }
    return (
      <Badge bg="secondary" className="small" title="За последние 24 часа виджет не подключался">
        Не установлен
      </Badge>
    );
  })();

  return (
    <>
      <Alert variant="info" className="py-2 small mb-3">
        <strong>Чат проекта «{projectName}».</strong> Передайте snippet клиенту — он
        вставляет его в <code>&lt;head&gt;</code> своего сайта или WebView мобильного
        приложения. После загрузки в правом нижнем углу появится виджет «Поддержка».
      </Alert>

      {loadingState === 'error' && errorMsg && (
        <Alert variant="danger" className="py-2 small mb-3">
          {errorMsg}{' '}
          <Button size="sm" variant="link" className="p-0 align-baseline" onClick={() => void loadAll()}>
            Повторить
          </Button>
        </Alert>
      )}

      {loadingState === 'not_created' && (
        <Alert variant="warning" className="py-2 small mb-3 d-flex align-items-center justify-content-between">
          <span>Проект ещё не зарегистрирован на chat-backend.</span>
          <Button size="sm" variant="primary" onClick={() => void handleCreate()}>
            Зарегистрировать
          </Button>
        </Alert>
      )}

      <Row className="g-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label className="small">Project ID</Form.Label>
            <InputGroup size="sm">
              <Form.Control readOnly value={projectId} />
              <Button
                variant={copiedKey === 'pid' ? 'success' : 'outline-secondary'}
                onClick={() => copy(projectId, 'pid')}
              >
                {copiedKey === 'pid' ? 'Скопировано' : 'Копировать'}
              </Button>
            </InputGroup>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label className="small d-flex justify-content-between align-items-center">
              <span>Widget shared secret</span>
              {statusBadge}
            </Form.Label>
            <InputGroup size="sm">
              <Form.Control
                readOnly
                placeholder={
                  loadingState === 'loading'
                    ? 'Загрузка…'
                    : 'Не получен — нажмите «Зарегистрировать»'
                }
                value={sharedSecret}
              />
              <Button
                variant={copiedKey === 'sec' ? 'success' : 'outline-secondary'}
                disabled={!sharedSecret}
                onClick={() => copy(sharedSecret, 'sec')}
              >
                {copiedKey === 'sec' ? 'Скопировано' : 'Копировать'}
              </Button>
              <Button
                variant="outline-danger"
                disabled={!sharedSecret || regenLoading}
                onClick={() => setShowRegenConfirm(true)}
                title="Перевыпустить — старые embed-snippet'ы перестанут работать"
              >
                {regenLoading ? <Spinner animation="border" size="sm" /> : 'Перевыпустить'}
              </Button>
            </InputGroup>
            <Form.Text className="text-muted">
              chat-backend хранит secret в <code>projects.widget_shared_secret</code>.
              Перевыпуск — destructive: после него старые embed-snippet'ы у клиентов
              перестанут подключаться, нужно раздать новый snippet.
            </Form.Text>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label className="small">Widget script URL</Form.Label>
            <InputGroup size="sm">
              <Form.Control readOnly value={widgetUrl} />
              <Button
                variant={copiedKey === 'wurl' ? 'success' : 'outline-secondary'}
                onClick={() => copy(widgetUrl, 'wurl')}
              >
                {copiedKey === 'wurl' ? 'Скопировано' : 'Копировать'}
              </Button>
            </InputGroup>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label className="small">Backend URL</Form.Label>
            <InputGroup size="sm">
              <Form.Control readOnly value={backendUrl} />
              <Button
                variant={copiedKey === 'burl' ? 'success' : 'outline-secondary'}
                onClick={() => copy(backendUrl, 'burl')}
              >
                {copiedKey === 'burl' ? 'Скопировано' : 'Копировать'}
              </Button>
            </InputGroup>
          </Form.Group>
        </Col>
      </Row>

      <Card className="mt-3">
        <Card.Header className="d-flex justify-content-between align-items-center py-2">
          <span className="small fw-semibold">Embed snippet</span>
          <Button
            size="sm"
            variant={copiedKey === 'snip' ? 'success' : 'outline-primary'}
            onClick={() => copy(snippet, 'snip')}
          >
            {copiedKey === 'snip' ? 'Скопировано' : 'Копировать snippet'}
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          <pre
            className="mb-0 p-3"
            style={{
              backgroundColor: '#f7f8fa',
              fontSize: '0.78rem',
              fontFamily: 'ui-monospace, SFMono-Regular, monospace',
              whiteSpace: 'pre',
              overflowX: 'auto'
            }}
          >
            {snippet}
          </pre>
        </Card.Body>
      </Card>

      {!sharedSecret && loadingState !== 'loading' && (
        <Alert variant="warning" className="py-2 small mt-3 mb-0">
          Snippet содержит placeholder <code>{SECRET_PLACEHOLDER}</code> — без реального
          secret виджет не подключится к chat-backend.
        </Alert>
      )}

      <Modal show={showRegenConfirm} onHide={() => setShowRegenConfirm(false)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">Перевыпустить widget secret?</Modal.Title>
        </Modal.Header>
        <Modal.Body className="small">
          После перевыпуска <strong>старые embed snippet'ы перестанут работать</strong>.
          Раздайте клиентам новый snippet после операции.
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" variant="outline-secondary" onClick={() => setShowRegenConfirm(false)}>
            Отмена
          </Button>
          <Button size="sm" variant="danger" onClick={() => void handleRegenerate()}>
            Перевыпустить
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ChatWidgetTab;
